<template>
  <div class="delivery-order-details-wrapper">
    <!-- Start Breadcrumb -->
    <div class="mb-3">
      <Breadcrumb :items="items" />
    </div>
    <!-- End Breadcrumb -->

    <!-- Start Main Loader -->
    <MainLoader key="loader" v-if="loading" />
    <!-- End Main Loader -->

    <!-- ===== Start Showing Order Details ===== -->
    <div key="main" v-else class="main-order-content">
      <!-- Top Modern Floating Header Bar -->
      <div class="order-hero-card mb-4">
        <div
          class="d-flex flex-wrap justify-content-between align-items-center gap-3"
        >
          <!-- Order ID & Status Summary -->
          <div class="d-flex align-items-center flex-wrap gap-3" v-if="allData">
            <div class="order-id-badge" v-if="allData.order_number">
              <i class="far fa-box-check ml-2 text-primary"></i>
              <span>تفاصيل طلب: </span>
              <strong class="order-num">#{{ allData.order_number }}</strong>
            </div>

            <div class="status-badge-chip" v-if="allData.status_trans">
              <span class="pulse-indicator"></span>
              {{ allData.status_trans }}
            </div>

            <div class="created-time-tag" v-if="allData.created_at">
              <i class="far fa-clock ml-1"></i>
              <span>{{ allData.created_at }}</span>
            </div>
          </div>

          <!-- Dynamic Action Buttons & Control Bar -->
          <div class="d-flex flex-wrap align-items-center gap-2" v-if="allData">
            <!-- Balance Transfer Button -->
            <v-btn
              depressed
              color="success"
              class="custom-hero-btn text-white rounded-pill px-4"
              :loading="balanceLoading"
              @click="isChangeBalanceModel = true"
            >
              <i class="far fa-wallet ml-2"></i>
              تحويل رصيد
            </v-btn>

            <!-- Assign Driver Select -->
            <div
              class="select-driver-container"
              v-if="
                allData.status != 'driver_finished_package' &&
                allData.status != 'client_cancel' &&
                allData.status != 'admin_cancel' &&
                allData.status != 'admin_returned'
              "
            >
              <v-select
                label="تعيين سائق"
                solo
                dense
                hide-details
                class="rounded-pill driver-select-field"
                :items="drivers"
                item-text="name"
                item-value="id"
                @input="assignDriver"
              ></v-select>
            </div>

            <!-- Finish Order Button -->
            <v-btn
              v-if="allData.status == 'driver_pre_finished'"
              @click="showPayType = true"
              color="success"
              depressed
              class="custom-hero-btn text-white rounded-pill px-4"
            >
              <i class="far fa-check-circle ml-2"></i>
              إنهاء الطلب
            </v-btn>

            <!-- Raise Invoice Button -->
            <v-btn
              v-if="allData.status == 'driver_pre_finished'"
              @click="invoiceDialog = true"
              color="#783d73"
              depressed
              class="custom-hero-btn text-white rounded-pill px-4"
            >
              <i class="far fa-file-invoice-dollar ml-2"></i>
              رفع فاتورة
            </v-btn>

            <!-- Change Order Status Button -->
            <v-btn
              v-if="allData.next_available_status"
              @click="changeOrderStatus"
              :disabled="isChangeStatusLoading"
              color="#783d73"
              depressed
              class="custom-hero-btn text-white rounded-pill px-4"
            >
              <i class="far fa-exchange-alt ml-2"></i>
              {{
                isChangeStatusLoading
                  ? "جاري التحميل..."
                  : "تغيير الحالة إلى " +
                    getNextStatusLabel(allData.next_available_status)
              }}
            </v-btn>

            <!-- Cancel Order Button -->
            <v-btn
              @click="cancelOrder"
              v-if="
                allData.status != 'driver_finished_package' &&
                allData.status != 'client_cancel' &&
                allData.status != 'admin_cancel' &&
                allData.status != 'admin_returned'
              "
              color="error"
              depressed
              class="custom-hero-btn text-white rounded-pill px-4"
            >
              <i class="far fa-times-circle ml-2"></i>
              إلغاء الطلب
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Quick Metrics Summary Grid -->
      <div class="row mb-4" v-if="allData">
        <div class="col-6 col-md-3">
          <div class="kpi-metric-card">
            <div class="kpi-icon icon-purple">
              <i class="far fa-box-open"></i>
            </div>
            <div class="kpi-info">
              <span class="kpi-label">نوع الطرد</span>
              <h6 class="kpi-value mb-0">
                {{
                  allData.package_type ||
                  allData.package_category?.name ||
                  "طرد عادي"
                }}
              </h6>
            </div>
          </div>
        </div>

        <div class="col-6 col-md-3">
          <div class="kpi-metric-card">
            <div class="kpi-icon icon-blue">
              <i class="far fa-map-marker-alt"></i>
            </div>
            <div class="kpi-info">
              <span class="kpi-label">الوقت والمتوقع</span>
              <h6 class="kpi-value mb-0">
                {{ allData.expected_time || 0 }} دقيقة |
                {{ allData.expected_distance || 0 }} كم
              </h6>
            </div>
          </div>
        </div>

        <div class="col-6 col-md-3">
          <div class="kpi-metric-card">
            <div class="kpi-icon icon-green">
              <i class="far fa-money-bill-wave"></i>
            </div>
            <div class="kpi-info">
              <span class="kpi-label">الإجمالي المدفوع</span>
              <h6 class="kpi-value price-text mb-0">
                {{ allData.total_price || 0 }}
              </h6>
            </div>
          </div>
        </div>

        <div class="col-6 col-md-3">
          <div class="kpi-metric-card">
            <div class="kpi-icon icon-orange">
              <i class="far fa-credit-card"></i>
            </div>
            <div class="kpi-info">
              <span class="kpi-label">طريقة وحالة الدفع</span>
              <h6 class="kpi-value mb-0">
                {{ allData.payment_type == "cash" ? "نقدي" : "أون لاين" }}
                <span
                  class="badge-status-sm"
                  :class="allData.is_paid ? 'bg-success' : 'bg-warning'"
                >
                  {{ allData.is_paid ? "مدفوع" : "غير مدفوع" }}
                </span>
              </h6>
            </div>
          </div>
        </div>
      </div>

      <!-- Horizontal Order Status Timeline Stepper -->
      <div
        class="modern-card mb-4 timeline-card-wrapper"
        v-if="
          allData &&
          allData.status_times &&
          Object.keys(allData.status_times).length
        "
      >
        <div
          class="card-header-sm d-flex justify-content-between align-items-center"
        >
          <h6 class="title mb-0">
            <i class="far fa-stream ml-2 text-primary"></i>
            توقيتات وحالات الطلب (الخط الزمني)
          </h6>
          <span
            class="badge badge-pill bg-light-primary text-primary px-3 py-1 font-weight-bold"
          >
            {{ Object.keys(allData.status_times).length }} مراحل
          </span>
        </div>
        <div class="card-body-sm p-4 overflow-hidden">
          <div class="horizontal-timeline-scroll">
            <div class="horizontal-stepper">
              <div
                class="stepper-item"
                v-for="(time, key, idx) in allData.status_times"
                :key="'h-step-' + key"
              >
                <div
                  class="stepper-line"
                  v-if="idx < Object.keys(allData.status_times).length - 1"
                ></div>
                <div class="stepper-icon-circle">
                  <i class="far fa-check"></i>
                </div>
                <div class="stepper-details">
                  <span class="stepper-title">{{
                    getStatusTimeLabel(key)
                  }}</span>
                  <span class="stepper-time">{{ formatStatusTime(time) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Layout Grid -->
      <div class="row" v-if="allData">
        <!-- Sidebar Column: Profiles & Media (3 Cols on Desktop) -->
        <div class="col-lg-4 col-xl-3 mb-4">
          <!-- Client Card -->
          <div class="modern-card profile-side-card mb-4" v-if="allData.client">
            <div class="card-header-sm">
              <h6 class="title mb-0">
                <i class="far fa-user ml-2 text-primary"></i>
                بيانات العميل
              </h6>
            </div>
            <div class="card-body-sm text-center">
              <router-link
                :to="`/client/show/${allData.client.id}`"
                class="profile-avatar-wrap mb-2"
              >
                <v-avatar size="80" class="profile-avatar">
                  <img :src="allData.client.avatar" alt="Client Avatar" />
                </v-avatar>
              </router-link>
              <h6 class="font-weight-bold mb-1">
                {{ allData.client.full_name }}
              </h6>
              <a
                :href="`tel:${allData.client.phone_complete_form}`"
                class="phone-link text-primary font-weight-bold d-inline-flex align-items-center gap-1 dir-ltr"
              >
                <i class="far fa-phone"></i>
                <span>{{ allData.client.phone_complete_form }}</span>
              </a>
            </div>
          </div>

          <!-- Driver Card -->
          <div class="modern-card profile-side-card mb-4" v-if="allData.driver">
            <div class="card-header-sm">
              <h6 class="title mb-0">
                <i class="far fa-steering-wheel ml-2 text-primary"></i>
                بيانات السائق
              </h6>
            </div>
            <div class="card-body-sm text-center">
              <router-link
                :to="`/driver/show/${allData.driver.id}`"
                class="profile-avatar-wrap mb-2"
              >
                <v-avatar size="80" class="profile-avatar">
                  <img :src="allData.driver.avatar" alt="Driver Avatar" />
                </v-avatar>
              </router-link>
              <h6 class="font-weight-bold mb-1">
                {{ allData.driver.full_name }}
              </h6>
              <div
                class="d-flex justify-content-center align-items-center flex-wrap gap-2 mb-2"
              >
                <span class="badge badge-light-secondary">
                  النوع:
                  {{
                    allData.driver && allData.driver.gender
                      ? allData.driver.gender == "male"
                        ? "ذكر"
                        : "أنثى"
                      : allData.gender == "male"
                      ? "ذكر"
                      : "أنثى"
                  }}
                </span>
                <span class="badge badge-light-secondary">
                  عدد الطلبات الحالية :
                  {{
                    allData.driver.active_orders_count != null
                      ? allData.driver.active_orders_count
                      : 0
                  }}
                </span>
              </div>
              <div
                class="dir-ltr"
                v-if="allData.driver.phone_code && allData.driver.phone"
              >
                <a
                  :href="`tel:${
                    allData.driver.phone_code + allData.driver.phone
                  }`"
                  class="phone-link text-primary font-weight-bold d-inline-flex align-items-center gap-1"
                >
                  <i class="far fa-phone"></i>
                  <span>{{
                    allData.driver.phone_code + allData.driver.phone
                  }}</span>
                </a>
              </div>
              <v-btn
                small
                depressed
                color="error"
                class="mt-3 text-white rounded-pill px-3"
                @click="driverFineDialog = true"
              >
                <i class="far fa-exclamation-triangle ml-1"></i>
                تطبيق مخالفة على السائق
              </v-btn>
            </div>
          </div>

          <!-- Store Card -->
          <div
            class="modern-card profile-side-card mb-4"
            v-if="allData.resturant"
          >
            <div class="card-header-sm">
              <h6 class="title mb-0">
                <i class="far fa-store ml-2 text-primary"></i>
                معلومات المتجر
              </h6>
            </div>
            <div class="card-body-sm text-center">
              <router-link
                :to="`/store/show/${allData.resturant.id}`"
                class="profile-avatar-wrap mb-2"
              >
                <div class="store-icon-wrap">
                  <img :src="allData.resturant.icon" alt="Store Icon" />
                </div>
              </router-link>
              <h6 class="font-weight-bold mb-1">
                {{ allData.resturant.name }}
              </h6>
            </div>
          </div>

          <!-- License Image Card -->
          <div class="modern-card mb-4" v-if="allData.license_image">
            <div class="card-header-sm">
              <h6 class="title mb-0">
                <i class="far fa-id-card ml-2 text-primary"></i>
                صورة الرخصة
              </h6>
            </div>
            <div class="card-body-sm text-center">
              <div class="media-preview-box">
                <img
                  :src="allData.license_image"
                  @error="replaceImage"
                  alt="License Image"
                />
              </div>
            </div>
          </div>

          <!-- Personal ID Card -->
          <div class="modern-card mb-4" v-if="allData.personal_id_image">
            <div class="card-header-sm">
              <h6 class="title mb-0">
                <i class="far fa-user-badge ml-2 text-primary"></i>
                صورة الهوية / العميل
              </h6>
            </div>
            <div class="card-body-sm text-center">
              <div class="media-preview-box">
                <img
                  :src="allData.personal_id_image"
                  @error="replaceImage"
                  alt="Personal ID Image"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Main Column: Route Map & Details Cards (9 Cols on Desktop) -->
        <div class="col-lg-8 col-xl-9">
          <!-- Route Map Card -->
          <div class="modern-card map-card mb-4" v-if="center">
            <div
              class="card-header-sm d-flex justify-content-between align-items-center"
            >
              <h6 class="title mb-0">
                <i class="far fa-route ml-2 text-primary"></i>
                موقع وخط سير الرحلة
              </h6>
            </div>
            <div class="card-body-sm p-0 overflow-hidden">
              <GmapMap
                style="width: 100%; height: 420px"
                :center="center"
                :zoom="8"
                :draggable="true"
                map-type-id="terrain"
                :heading="90"
                :tilt="45"
                @click="handleMapClick"
              >
                <div>
                  <gmap-polyline
                    v-if="
                      allData &&
                      allData.expected_route &&
                      allData.expected_route.length > 0
                    "
                    :path="allData.expected_route"
                    :options="{
                      strokeColor: '#783d73',
                      strokeWeight: 5,
                      strokeOpacity: 0.8,
                    }"
                  />
                  <GmapMarker
                    v-if="
                      allData.expected_route &&
                      allData.expected_route.length > 0
                    "
                    :position="allData.expected_route[0]"
                    :icon="require('@/assets/images/order.png')"
                  />
                  <GmapMarker
                    v-if="
                      allData.expected_route &&
                      allData.expected_route.length > 0
                    "
                    :position="
                      allData.expected_route[allData.expected_route.length - 1]
                    "
                    :icon="require('@/assets/images/markers/package_end.png')"
                  />
                  <GmapMarker
                    v-if="marker"
                    :icon="image"
                    :position="marker"
                    @click="
                      goToPartnerPage(allData.driver ? allData.driver.id : '')
                    "
                  />
                </div>
              </GmapMap>
            </div>
          </div>

          <!-- General Order Info Card -->
          <div class="modern-card mb-4">
            <div class="card-header-sm">
              <h6 class="title mb-0">
                <i class="far fa-info-circle ml-2 text-primary"></i>
                معلومات عن الطلب
              </h6>
            </div>
            <div class="card-body-sm">
              <div class="info-grid-list">
                <div class="info-item">
                  <span class="info-label">الحالة</span>
                  <span class="info-value status-tag">{{
                    allData.status_trans
                  }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">نوع الطلب</span>
                  <span class="info-value">{{
                    allData.order_type == "delivery" ? "توصيل" : "طرد"
                  }}</span>
                </div>
                <div class="info-item" v-if="allData.take_date">
                  <span class="info-label">تاريخ التوصيل</span>
                  <span class="info-value">{{ allData.take_date }}</span>
                </div>
                <div class="info-item" v-if="allData.expected_time">
                  <span class="info-label">الوقت المتوقع</span>
                  <span class="info-value"
                    >{{ allData.expected_time }} دقيقة</span
                  >
                </div>
                <div class="info-item" v-if="allData.created_at">
                  <span class="info-label">تاريخ إنشاء الطلب</span>
                  <span class="info-value dir-ltr">{{
                    allData.created_at
                  }}</span>
                </div>
                <div class="info-item" v-if="allData.package_type">
                  <span class="info-label">نوع الطرد</span>
                  <span class="info-value">{{ allData.package_type }}</span>
                </div>
                <div class="info-item" v-if="allData.package_status">
                  <span class="info-label">حالة الطرد</span>
                  <span class="info-value">{{ allData.package_status }}</span>
                </div>
                <div class="info-item" v-if="allData.number">
                  <span class="info-label">رقم الطرد</span>
                  <span class="info-value">#{{ allData.number }}</span>
                </div>
                <div class="info-item" v-if="allData.loader_number">
                  <span class="info-label">رقم الحمول</span>
                  <span class="info-value">{{ allData.loader_number }}</span>
                </div>
                <div
                  class="info-item"
                  v-if="allData.is_need_install !== undefined"
                >
                  <span class="info-label">تثبيت</span>
                  <span class="info-value">{{
                    allData.is_need_install ? "نعم" : "لا"
                  }}</span>
                </div>
                <div class="info-item" v-if="allData.package_category">
                  <span class="info-label">فئة الطرد</span>
                  <span class="info-value">{{
                    allData.package_category?.name
                  }}</span>
                </div>
                <div class="info-item" v-if="allData.sub_total">
                  <span class="info-label">الإجمالي قبل الخصم</span>
                  <span class="info-value">{{ allData.sub_total }}</span>
                </div>
                <div class="info-item" v-if="allData.additional_note">
                  <span class="info-label">ملاحظة إضافية</span>
                  <span class="info-value">{{ allData.additional_note }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">حالة الدفع</span>
                  <span
                    class="info-value"
                    :class="
                      allData.is_paid
                        ? 'text-success font-weight-bold'
                        : 'text-danger'
                    "
                  >
                    {{ allData.is_paid ? "مدفوع" : "غير مدفوع" }}
                  </span>
                </div>
                <div class="info-item">
                  <span class="info-label">طريقة الدفع</span>
                  <span class="info-value">{{
                    allData.payment_type == "cash" ? "نقدي" : "أون لاين"
                  }}</span>
                </div>
              </div>

              <!-- Reason of Finish Box -->
              <div
                class="finished-reason-box mt-3 p-3 bg-light rounded-lg"
                v-if="allData.reason_of_finished"
              >
                <h6 class="font-weight-bold text-primary mb-1">
                  <i class="far fa-info-circle ml-1"></i>
                  سبب الإنهاء:
                </h6>
                <p class="mb-0 text-muted">{{ allData.reason_of_finished }}</p>
              </div>
            </div>
          </div>

          <!-- Reason of Rejection/Cancellation Card -->
          <div
            class="modern-card mb-4 border-left-danger"
            v-if="allData.cancel_reason"
          >
            <div class="card-header-sm">
              <span class="title text-danger"
                ><i class="far fa-times-circle ml-2"></i>سبب الإلغاء /
                الرفض</span
              >
            </div>
            <div class="card-body-sm">
              <h6 class="font-weight-bold text-danger mb-2">
                {{ allData.cancel_reason.name }}
              </h6>
              <div v-if="allData.cancel_reason_text">
                <span class="text-muted small">تعليق العميل:</span>
                <p class="mb-0 text-dark bg-light p-2 rounded mt-1">
                  {{ allData.cancel_reason_text }}
                </p>
              </div>
            </div>
          </div>

          <!-- Payment Breakdown Card -->
          <div class="modern-card receipt-card mb-4">
            <div class="card-header-sm">
              <h6 class="title mb-0">
                <i class="far fa-file-invoice-dollar ml-2 text-primary"></i>
                معلومات الحساب والدفع
              </h6>
            </div>
            <div class="card-body-sm">
              <div class="receipt-list">
                <div class="receipt-row" v-if="allData.payment_type">
                  <span class="label">نوع الدفع</span>
                  <span class="value font-weight-bold">{{
                    allData.payment_type == "cash" ? "نقدي" : "أون لاين"
                  }}</span>
                </div>
                <div class="receipt-row" v-if="allData.shiping_tax">
                  <span class="label">الضريبة</span>
                  <span class="value">{{ allData.shiping_tax }}</span>
                </div>
                <div class="receipt-row" v-if="allData.service_fee">
                  <span class="label">رسوم الخدمة</span>
                  <span class="value">{{ allData.service_fee }}</span>
                </div>
                <div class="receipt-row" v-if="allData.shiping">
                  <span class="label">تكلفة الشحن والتوصيل</span>
                  <span class="value">{{ allData.shiping }}</span>
                </div>
                <div
                  class="receipt-row text-success"
                  v-if="allData.coupon_amount"
                >
                  <span class="label">خصم الكوبون</span>
                  <span class="value">-{{ allData.coupon_amount }}</span>
                </div>

                <div class="receipt-total-box my-3">
                  <span class="label">المدفوع من قبل العميل</span>
                  <span class="total-amount">{{ allData.total_price }}</span>
                </div>

                <div
                  class="receipt-row text-muted small"
                  v-if="allData.app_amount_price"
                >
                  <span class="label"
                    >قيمة ضريبة التطبيق ({{ allData.app_amount }} %)</span
                  >
                  <span class="value">{{ allData.app_amount_price }}</span>
                </div>
                <div
                  class="receipt-row text-muted small"
                  v-if="allData.company_driver_app_amount_price"
                >
                  <span class="label"
                    >قيمة ضريبة شركة المناديب ({{
                      allData.company_driver_app_amount
                    }}
                    %)</span
                  >
                  <span class="value">{{
                    allData.company_driver_app_amount_price
                  }}</span>
                </div>
                <div
                  class="receipt-row text-muted small"
                  v-if="allData.restaurant_app_amount_price"
                >
                  <span class="label"
                    >قيمة ضريبة المتجر ({{
                      allData.restaurant_app_amount
                    }}
                    %)</span
                  >
                  <span class="value">{{
                    allData.restaurant_app_amount_price
                  }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Driver Candidates Table -->
          <div class="modern-card mb-4" v-if="orderCandidates.length">
            <div class="card-header-sm">
              <span class="title"
                ><i class="far fa-users ml-2"></i>المرشحون للطلب
                <span class="badge badge-pill badge-light-warning ml-1">{{
                  orderCandidates.length
                }}</span></span
              >
            </div>
            <div class="p-0">
              <order-candidates
                :candidates="orderCandidates"
                :order-id="id"
                order-type="package"
                :search-started-at="allData.driver_search_started_at"
              />
            </div>
          </div>

          <!-- Combined Tables Section with Tabs Navigation -->
          <div
            class="modern-card mb-4 tabs-section-card"
            v-if="
              allData &&
              (allData.wallet_transactions ||
                allData.order_logs ||
                (allData.call_logs && allData.call_logs.length))
            "
          >
            <!-- Tabs Navigation Bar -->
            <div class="tabs-header-bar p-2 border-bottom bg-light">
              <div class="nav nav-pills nav-justified gap-2 custom-nav-pills">
                <button
                  type="button"
                  class="nav-link custom-tab-btn"
                  :class="{ active: activeLogTab === 'wallet' }"
                  @click="activeLogTab = 'wallet'"
                  v-if="allData.wallet_transactions"
                >
                  <i class="far fa-wallet ml-2"></i>
                  عمليات المحفظة المرتبطة
                  <span
                    class="badge badge-pill badge-light-primary ml-1"
                    v-if="allData.wallet_transactions"
                  >
                    {{ allData.wallet_transactions.length }}
                  </span>
                </button>

                <button
                  type="button"
                  class="nav-link custom-tab-btn"
                  :class="{ active: activeLogTab === 'logs' }"
                  @click="activeLogTab = 'logs'"
                  v-if="allData.order_logs"
                >
                  <i class="far fa-history ml-2"></i>
                  العمليات على الطلب
                  <span
                    class="badge badge-pill badge-light-info ml-1"
                    v-if="allData.order_logs"
                  >
                    {{ allData.order_logs.length }}
                  </span>
                </button>

                <button
                  type="button"
                  class="nav-link custom-tab-btn"
                  :class="{ active: activeLogTab === 'calls' }"
                  @click="activeLogTab = 'calls'"
                  v-if="allData.call_logs && allData.call_logs.length"
                >
                  <i class="far fa-phone-alt ml-2"></i>
                  سجل المكالمات
                  <span
                    class="badge badge-pill badge-light-warning ml-1"
                    v-if="allData.call_logs"
                  >
                    {{ allData.call_logs.length }}
                  </span>
                </button>
              </div>
            </div>

            <!-- Tab Content 1: Wallet Transactions Table -->
            <div
              class="card-body-sm p-0"
              v-if="activeLogTab === 'wallet' && allData.wallet_transactions"
            >
              <v-data-table
                class="thumb strip wallet-transactions-table border-0"
                :headers="headers2"
                :items="allData.wallet_transactions"
                :custom-filter="helper_filterSearch"
                :loading-text="$t('table.loadingData')"
                item-key="id"
                hide-default-footer
                :items-per-page="-1"
                :item-class="getWalletRowClass"
              >
                <template v-slot:no-data>
                  {{ $t("table.noData") }}
                </template>

                <template v-slot:[`item.index`]="{ index }">
                  #{{ index + 1 }}
                </template>
                <template v-slot:[`item.meal`]="{ item }">
                  <router-link
                    :to="`/products/show/${item.meal.id}`"
                    v-if="item.meal"
                  >
                    <img :src="item.meal.image" class="image" alt="" />
                    <div>{{ item.meal.name }}</div>
                  </router-link>
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                  <div class="_actions">
                    <v-icon class="show" small @click="showItem(item)">
                      fal fa-eye
                    </v-icon>
                  </div>
                </template>
              </v-data-table>
            </div>

            <!-- Tab Content 2: Order Logs Table -->
            <div
              class="card-body-sm p-0"
              v-if="activeLogTab === 'logs' && allData.order_logs"
            >
              <v-data-table
                class="thumb strip border-0"
                :headers="headers3"
                :items="allData.order_logs"
                :loading-text="$t('table.loadingData')"
                item-key="id"
                hide-default-footer
                :items-per-page="-1"
              >
                <template v-slot:no-data>
                  {{ $t("table.noData") }}
                </template>

                <template v-slot:[`item.index`]="{ index }">
                  #{{ index + 1 }}
                </template>
                <template v-slot:[`item.meal`]="{ item }">
                  <router-link
                    :to="`/products/show/${item.meal.id}`"
                    v-if="item.meal"
                  >
                    <img :src="item.meal.image" class="image" alt="" />
                    <div>{{ item.meal.name }}</div>
                  </router-link>
                </template>
                <template v-slot:[`item.type`]="{ item }">
                  <span
                    v-if="item.type == 'deposit'"
                    class="badge bg-success text-white"
                    >إيداع</span
                  >
                  <span
                    v-if="item.type == 'to_withdraw'"
                    class="badge bg-warning text-dark"
                    >سحب</span
                  >
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                  <div class="_actions">
                    <v-icon class="show" small @click="showItem(item)">
                      fal fa-eye
                    </v-icon>
                  </div>
                </template>
              </v-data-table>
            </div>

            <!-- Tab Content 3: Call Logs Table -->
            <div
              class="card-body-sm p-0"
              v-if="
                activeLogTab === 'calls' &&
                allData.call_logs &&
                allData.call_logs.length
              "
            >
              <v-data-table
                class="thumb strip border-0"
                :headers="headers4"
                :items="allData.call_logs"
                :loading-text="$t('table.loadingData')"
                item-key="id"
                hide-default-footer
                :items-per-page="-1"
              >
                <template v-slot:no-data>
                  {{ $t("table.noData") }}
                </template>

                <template v-slot:[`item.index`]="{ index }">
                  #{{ index + 1 }}
                </template>

                <template v-slot:[`item.caller`]="{ item }">
                  <div
                    class="d-flex align-center justify-center gap-2"
                    v-if="item.caller"
                  >
                    <div class="text-right">
                      <div class="font-weight-bold">
                        {{ item.caller.full_name }}
                      </div>
                      <div class="small text-muted dir-ltr">
                        +{{ item.caller.phone_code }}{{ item.caller.phone }}
                      </div>
                    </div>
                  </div>
                </template>

                <template v-slot:[`item.receiver`]="{ item }">
                  {{ item.receiver ? item.receiver.full_name : "-" }}
                </template>

                <template v-slot:[`item.caller.user_type`]="{ item }">
                  <v-chip
                    small
                    :color="
                      item.caller && item.caller.user_type === 'client'
                        ? 'blue'
                        : item.caller && item.caller.user_type === 'driver'
                        ? 'green'
                        : 'orange'
                    "
                    text-color="white"
                  >
                    {{
                      item.caller && item.caller.user_type === "client"
                        ? "عميل"
                        : item.caller && item.caller.user_type === "driver"
                        ? "سائق"
                        : item.caller
                        ? item.caller.user_type
                        : ""
                    }}
                  </v-chip>
                </template>
              </v-data-table>
            </div>
          </div>

          <!-- Chat Section Card -->
          <div class="modern-card chat-section-card mb-4" v-if="chatData">
            <div class="card-header-sm">
              <h6 class="title mb-0">
                <i class="far fa-comments ml-2 text-primary"></i>
                سجل المحادثة
              </h6>
            </div>
            <div class="card-body-sm p-3">
              <ChatComp readOnly :messages="chatData" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modals & Dialogs -->

    <!-- Finish Order Payment Type Modal -->
    <v-dialog v-model="showPayType" width="400">
      <v-card class="p-4 modern-dialog-card">
        <form @submit.prevent="finishOrder">
          <h5 class="main_color text-center mb-3 font-weight-bold">
            اختر طريقة الدفع (اختياري)
          </h5>
          <div class="input_wrapper top_label mb-3">
            <multiselect
              v-model="payment_type"
              :options="payment_types"
              label="name"
              track-by="name"
              placeholder=" "
              :searchable="true"
              :allow-empty="false"
              :show-labels="false"
            ></multiselect>
          </div>
          <div class="input_wrapper top_label mb-4">
            <textarea
              class="form-control custom-textarea-field"
              rows="3"
              v-model="note"
              required
            />
            <label class="form-label"> الملاحظة </label>
          </div>
          <div class="text-center">
            <v-btn
              depressed
              color="success"
              class="px-10 rounded-pill text-white"
              :loading="isFinishOrderLoading"
              type="submit"
            >
              إرسال
            </v-btn>
          </div>
        </form>
      </v-card>
    </v-dialog>

    <!-- Driver Fine Dialog -->
    <v-dialog v-model="driverFineDialog" width="450">
      <v-card class="p-4 modern-dialog-card">
        <form @submit.prevent="submitDriverFine">
          <h5 class="main_color text-center mb-4 font-weight-bold">
            تطبيق مخالفة على السائق
          </h5>
          <div class="input_wrapper top_label mb-4">
            <label class="form-label">السبب*</label>
            <multiselect
              v-model="fineReasonObj"
              :options="cancelReasons"
              label="name"
              track-by="id"
              placeholder="اختر السبب"
              :searchable="true"
              :allow-empty="false"
              :show-labels="false"
            ></multiselect>
          </div>
          <div class="input_wrapper top_label mb-4">
            <textarea
              class="form-control custom-textarea-field"
              v-model="fineNote"
              rows="3"
            />
            <label class="form-label">ملاحظة</label>
          </div>
          <div class="text-center mt-4 d-flex justify-content-center gap-2">
            <v-btn
              depressed
              color="error"
              class="px-8 text-white rounded-pill"
              :loading="isSubmittingFine"
              type="submit"
            >
              إرسال
            </v-btn>
            <v-btn
              depressed
              class="px-8 rounded-pill"
              @click="driverFineDialog = false"
            >
              إلغاء
            </v-btn>
          </div>
        </form>
      </v-card>
    </v-dialog>

    <!-- Balance Transfer Dialog -->
    <v-dialog v-model="isChangeBalanceModel" width="420">
      <v-card class="p-4 modern-dialog-card">
        <form @submit.prevent="toggleBalance">
          <h5 class="main_color text-center mb-3 font-weight-bold">
            تحويل رصيد
          </h5>
          <div class="input_wrapper top_label mb-3">
            <label class="form-label"> نوع التحويل </label>
            <multiselect
              v-model="transaction.type"
              :options="types"
              label="name"
              track-by="name"
              placeholder=" "
              :searchable="true"
              :allow-empty="false"
              :show-labels="false"
            ></multiselect>
          </div>
          <div class="input_wrapper top_label mb-3">
            <label class="form-label"> تحويل ل </label>
            <multiselect
              v-model="user_type"
              :options="user_types"
              label="name"
              track-by="name"
              placeholder=" "
              :searchable="true"
              :allow-empty="false"
              :show-labels="false"
            ></multiselect>
          </div>
          <div class="input_wrapper top_label mb-3">
            <input
              step="any"
              type="number"
              class="form-control custom-input-field"
              v-model="transaction.amount"
              @input="helper_checkIfInputIsEmpty"
            />
            <label class="form-label"> المبلغ </label>
          </div>
          <div class="input_wrapper top_label mb-3">
            <input
              type="text"
              class="form-control custom-input-field"
              v-model="transaction.note"
              @input="helper_checkIfInputIsEmpty"
            />
            <label class="form-label"> الملاحظة </label>
          </div>
          <div class="input_wrapper top_label mb-4">
            <textarea
              class="form-control custom-textarea-field"
              rows="2"
              v-model="transaction.reason"
            />
            <label class="form-label"> السبب </label>
          </div>
          <div class="text-center">
            <v-btn
              depressed
              color="success"
              class="px-10 rounded-pill text-white"
              :loading="balanceLoading"
              type="submit"
            >
              تحويل
            </v-btn>
          </div>
        </form>
      </v-card>
    </v-dialog>

    <!-- Cancel Order Confirmation Dialog -->
    <v-dialog v-model="cancelDialog" max-width="480px">
      <v-card class="p-4 modern-dialog-card">
        <h5 class="text-center font-weight-bold mb-3">
          هل أنت متأكد من إلغاء الطلب؟
        </h5>
        <textarea
          v-model="cancel_reason"
          placeholder="أدخل سبب الإلغاء..."
          class="form-control custom-textarea-field mb-4"
          rows="3"
        ></textarea>
        <div class="d-flex justify-content-center gap-3">
          <v-btn
            color="#F44336"
            @click="cancelConfirm"
            class="text-white px-6 rounded-pill"
            :loading="cancelLoading"
            depressed
          >
            نعم، إلغاء
          </v-btn>
          <v-btn
            color="#1B5E20"
            @click="cancelDialog = false"
            class="text-white px-6 rounded-pill"
            depressed
          >
            تراجع
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Raise An Invoice Dialog -->
    <v-dialog v-model="invoiceDialog" width="450">
      <v-card class="p-4 modern-dialog-card">
        <form @submit.prevent="submitInvoice">
          <h5 class="main_color text-center mb-4 font-weight-bold">
            {{ lang === "ar" ? "رفع فاتورة" : "Raise An Invoice" }}
          </h5>

          <!-- Enter cost -->
          <div class="input_wrapper top_label mb-4">
            <input
              type="number"
              step="any"
              class="form-control custom-input-field"
              v-model="invoiceCost"
              :placeholder="
                lang === 'ar' ? 'أدخل مبلغ الفاتورة' : 'Enter the cost'
              "
              required
            />
            <label class="form-label"
              >{{ lang === "ar" ? "مبلغ الفاتورة" : "Cost" }}*</label
            >
          </div>

          <!-- Order Cost Details (Delivery Value, VAT, Service Fee, Total) -->
          <div
            class="border rounded-lg p-3 mb-4 bg-light"
            style="font-size: 13px"
          >
            <div class="d-flex justify-content-between mb-2">
              <span class="text-muted">{{
                lang === "ar" ? "قيمة التوصيل" : "Delivery Value"
              }}</span>
              <span class="font-weight-bold">{{
                allData?.shiping || allData?.delivery_price || 0
              }}</span>
            </div>
            <div class="d-flex justify-content-between mb-2">
              <span class="text-muted">{{
                lang === "ar" ? "الضريبة" : "VAT"
              }}</span>
              <span class="font-weight-bold">{{
                allData?.shiping_tax || allData?.tax || 0
              }}</span>
            </div>
            <div
              class="d-flex justify-content-between mb-2"
              v-if="allData?.service_fee"
            >
              <span class="text-muted">{{
                lang === "ar" ? "رسوم الخدمة" : "Service Fee"
              }}</span>
              <span class="font-weight-bold">{{ allData?.service_fee }}</span>
            </div>
            <div
              class="d-flex justify-content-between pt-2 border-top font-weight-bold"
            >
              <span>{{ lang === "ar" ? "الإجمالي" : "Total" }}</span>
              <span class="main_color">{{ allData?.total_price || 0 }}</span>
            </div>
          </div>

          <!-- File Upload Field -->
          <div class="mb-4">
            <div
              class="custom-file-upload-box text-center p-4"
              :class="{ 'has-preview': invoiceImagePreview }"
            >
              <input
                type="file"
                ref="invoiceFileInput"
                accept="image/*"
                class="d-none"
                @change="onInvoiceFileChange"
              />
              <div
                v-if="!invoiceImagePreview"
                class="upload-placeholder cursor-pointer"
                @click="$refs.invoiceFileInput.click()"
              >
                <i class="far fa-cloud-upload fa-2x mb-2 text-primary"></i>
                <div class="text-muted font-weight-bold">
                  {{
                    lang === "ar"
                      ? "اختر صورة الفاتورة"
                      : "Choose Invoice Image"
                  }}
                </div>
              </div>
              <div v-else class="upload-preview position-relative">
                <img
                  :src="invoiceImagePreview"
                  class="img-fluid rounded-lg shadow-sm max-h-200"
                />
                <button
                  type="button"
                  class="remove-upload-btn"
                  @click="clearInvoiceFile"
                >
                  <i class="far fa-times"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Action buttons -->
          <div class="d-flex gap-2">
            <v-btn
              depressed
              color="#783d73"
              class="text-white flex-grow-1 rounded-pill"
              :loading="isSubmittingInvoice"
              type="submit"
            >
              {{ lang === "ar" ? "تأكيد" : "Confirm" }}
            </v-btn>
            <v-btn
              depressed
              class="flex-grow-1 rounded-pill"
              @click="invoiceDialog = false"
            >
              {{ lang === "ar" ? "إلغاء" : "Cancel" }}
            </v-btn>
          </div>
        </form>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import imageFalse from "@/assets/images/icons/logo.png";
import sportCarImage from "@/assets/images/sport-car.png";
import ChatComp from "@/components/Global/ChatCompOrder";
import OrderCandidates from "@/components/Global/OrderCandidates";
import io from "socket.io-client";

export default {
  props: ["id"],
  name: "CarRental",

  components: {
    ChatComp,
    OrderCandidates,
  },

  data() {
    return {
      // ========== Breadcrumbs
      items: [
        {
          text: this.$t("breadcrumb.mainPage"),
          disabled: false,
          href: "/",
        },
        {
          text: this.$t("breadcrumb.orders.package_orders"),
          disabled: true,
          href: "/delivery_orders/show-all",
        },
      ],

      // ========== Your Data
      allData: null,

      // ========== Loading
      loading: false,
      reportReasonToShow: null,
      btnIsLoading: false,
      drivers: [],
      cancelDialog: false,
      cancelLoading: false,
      cancel_reason: null,
      isFinishOrderLoading: false,
      isChangeBalanceModel: false,
      balanceLoading: false,
      driverFineDialog: false,
      fineReasonObj: null,
      cancelReasons: [],
      fineNote: "",
      isSubmittingFine: false,
      transaction: {
        type: null,
        amount: null,
        note: null,
        reason: null,
      },
      types: [
        {
          id: "withdraw",
          name: "سحب",
        },
        {
          id: "charge",
          name: "ايداع",
        },
      ],
      showPayType: false,
      payment_type: {
        id: "cash",
        name: "كاش",
      },
      payment_types: [
        {
          id: "cash",
          name: "كاش",
        },
      ],
      user_types: [
        {
          id: "user",
          name: "العميل",
        },
        {
          id: "driver",
          name: "السائق",
        },
      ],
      user_type: null,
      clickedLatLng: null,
      marker: null,
      center: {
        lat: 24.7136,
        lng: 46.7385,
      },
      image: {
        url: sportCarImage,
        size: { width: 32, height: 32 },
      },
      note: null,
      invoiceDialog: false,
      invoiceCost: "",
      invoiceFile: null,
      invoiceImagePreview: null,
      isSubmittingInvoice: false,
      isUploadingImage: false,
      isChangeStatusLoading: false,
      chatData: null,
      activeLogTab: "wallet",
    };
  },

  computed: {
    ...mapGetters({
      lang: "lang_module/lang",
    }),
    orderCandidates() {
      const list = (this.allData && this.allData.candidates) || [];
      return Array.isArray(list) ? list : [];
    },
    headers() {
      return [
        {
          text: "#",
          align: "center",
          value: "index",
          sortable: true,
          width: "80",
        },
        {
          text: "المنتج",
          align: "center",
          value: "meal",
          sortable: false,
        },
        {
          text: "الكمية",
          align: "center",
          value: "quantity",
          sortable: false,
        },
        {
          text: "السعر",
          align: "center",
          value: "price",
          sortable: false,
        },
        {
          text: "السعر الإجمالي",
          align: "center",
          value: "total_price",
          sortable: false,
        },
      ];
    },
    headers2() {
      return [
        {
          text: "#",
          align: "center",
          value: "index",
          sortable: true,
          width: "80",
        },
        {
          text: "العنوان",
          align: "center",
          value: "title_trans",
          sortable: false,
        },
        {
          text: "الكمية",
          align: "center",
          value: "amount",
          sortable: false,
        },
        {
          text: "النوع",
          align: "center",
          value: "type",
          sortable: false,
        },
        {
          text: "نوع المستخدم",
          align: "center",
          value: "user.user_type",
          sortable: false,
        },
        {
          text: " التاريخ",
          align: "center",
          value: "created_at",
          sortable: false,
        },
        {
          text: " الحالة",
          align: "center",
          value: "status",
          sortable: false,
        },
      ];
    },
    headers3() {
      return [
        {
          text: "#",
          align: "center",
          value: "index",
          sortable: true,
          width: "80",
        },
        {
          text: "الموظف",
          align: "center",
          value: "user.full_name",
          sortable: false,
        },
        {
          text: "العملية",
          align: "center",
          value: "log",
          sortable: false,
        },
        {
          text: "النوع",
          align: "center",
          value: "type",
          sortable: false,
        },
        {
          text: " التاريخ",
          align: "center",
          value: "created_at",
          sortable: false,
        },
      ];
    },
    headers4() {
      return [
        {
          text: "#",
          align: "center",
          value: "index",
          sortable: false,
          width: "60",
        },
        {
          text: "المتصل",
          align: "center",
          value: "caller",
          sortable: false,
        },
        {
          text: "نوع المتصل",
          align: "center",
          value: "caller.user_type",
          sortable: false,
        },
        {
          text: "المستقبل",
          align: "center",
          value: "receiver",
          sortable: false,
        },
        {
          text: "التاريخ",
          align: "center",
          value: "created_at",
          sortable: false,
        },
      ];
    },
  },

  watch: {
    invoiceDialog(val) {
      if (!val) {
        this.invoiceCost = "";
        this.invoiceFile = null;
        this.invoiceImagePreview = null;
        if (this.$refs.invoiceFileInput) {
          this.$refs.invoiceFileInput.value = "";
        }
      }
    },
  },

  methods: {
    getStatusTimeLabel(key) {
      const statusLabels = {
        pending: "قيد الانتظار",
        client_cancel: "تم الإلغاء من قبل العميل",
        admin_cancel: "تم الإلغاء من قبل الإدارة",
        admin_returned: "تم الإرجاع من قبل الإدارة",
        restaurant_accept: "تم القبول من قبل المطعم",
        restaurant_reject: "تم الرفض من قبل المطعم",
        restaurant_ready: "تم تجهيز الطلب من المطعم",
        driver_accept: "تم القبول من السائق",
        driver_arrived: "وصل السائق للمتجر",
        driver_received: "استلم السائق الطلب",
        driver_start_order: "بدأ السائق",
        driver_pre_finished: "السائق أوشك على الانتهاء",
        driver_finished_delivery: "تم الانتهاء",
        driver_finished_package: "تم الانتهاء",
        driver_cancel: "تم الإلغاء من قبل السائق",
        driver_reject: "تم الرفض من قبل السائق",
        driver_rejected: "تم الرفض من قبل السائق",
        paid: "تم الدفع",
      };
      return statusLabels[key] || key.replace(/_/g, " ");
    },
    formatStatusTime(time) {
      if (!time) return "-";
      return new Date(time).toLocaleString();
    },
    getWalletRowClass(item) {
      if (item.user && item.user.user_type) {
        switch (item.user.user_type) {
          case "driver":
            return "wallet-row-driver";
          case "client":
            return "wallet-row-client";
          case "restaurant":
            return "wallet-row-restaurant";
          default:
            return "";
        }
      }
      return "";
    },
    handleMapClick(event) {
      this.clickedLatLng = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      };
      setTimeout(() => {
        this.mapLogic();
      }, 2000);
    },
    async finishOrder() {
      try {
        this.data = true;
        const requestData = {};
        if (this.payment_type && this.payment_type.id) {
          requestData.payment_type = this.payment_type.id;
          requestData.reason_of_finished = this.note;
        }
        const { data } = await this.$axios.post(
          `order/finished_package/${this.allData.id}`,
          requestData
        );
        this.$iziToast.success({
          timeout: 2000,
          message: data.message,
          position: "bottomRight",
        });
        this.isFinishOrderLoading = false;
        this.setRows();
      } catch (err) {
        this.$iziToast.error({
          timeout: 2000,
          message: err.response?.data?.message || "فشل إنهاء الطلب",
          position: "bottomRight",
        });
        this.isFinishOrderLoading = false;
      }
    },
    submitDriverFine() {
      if (!this.fineReasonObj) {
        this.$iziToast.error({
          message: "السبب مطلوب",
          position: "bottomRight",
        });
        return;
      }
      this.isSubmittingFine = true;
      this.$axios({
        method: "POST",
        url: `order/${this.id}/driver-fines`,
        data: {
          cancel_reason_id: this.fineReasonObj.id,
          reason: this.fineReasonObj.name,
          note: this.fineNote,
        },
      })
        .then((res) => {
          this.$iziToast.success({
            timeout: 2000,
            message: res.data.message || "تم تسجيل المخالفة بنجاح",
            position: "bottomRight",
          });
          this.driverFineDialog = false;
          this.fineReasonObj = null;
          this.fineNote = "";
          this.isSubmittingFine = false;
        })
        .catch((err) => {
          this.$iziToast.error({
            timeout: 2000,
            message: err.response?.data?.message || "فشل تسجيل المخالفة",
            position: "bottomRight",
          });
          this.isSubmittingFine = false;
        });
    },
    getCancelReasons() {
      this.$axios({
        method: "GET",
        url: "cancel_reason_not_paginated?reason_type=fine",
      })
        .then((res) => {
          this.cancelReasons = res.data.data;
        })
        .catch((err) => {
          console.error(err);
        });
    },
    toggleBalance() {
      let user_id;
      if (this.user_type && this.user_type.id == "user") {
        user_id = this.allData.client?.id;
      } else if (this.user_type && this.user_type.id == "driver") {
        user_id = this.allData.driver?.id;
      } else if (this.user_type && this.user_type.id == "store") {
        user_id = this.allData.resturant?.id;
      }
      this.balanceLoading = true;
      if (!this.transaction.type) {
        this.$iziToast.error({
          timeout: 2000,
          message: "ادخل نوع التحويل",
          position: "bottomCenter",
        });
        this.balanceLoading = false;
        return;
      }
      if (!this.transaction.amount) {
        this.balanceLoading = false;
        return;
      }
      const formDta = new FormData();
      formDta.append("type", this.transaction.type.id);
      formDta.append("model_type", "package");
      formDta.append("model_id", this.id);
      formDta.append("amount", this.transaction.amount);
      if (this.transaction.note) {
        formDta.append("note", this.transaction.note);
      }
      if (this.transaction.reason) {
        formDta.append("reason", this.transaction.reason);
      }

      this.$axios
        .post(`${this.transaction.type.id}/${user_id}`, formDta)
        .then((res) => {
          this.balanceLoading = false;
          this.isChangeBalanceModel = false;
          this.transaction.amount = null;
          this.transaction.type = null;
          this.user_type = null;
          this.transaction.note = null;
          this.transaction.reason = null;
          this.$iziToast.success({
            timeout: 2000,
            message: res.data.message,
            position: "bottomCenter",
          });
          this.$emit("get_item");
          this.setRows();
        })
        .catch((err) => {
          this.balanceLoading = false;
          this.$iziToast.error({
            timeout: 2000,
            message: err.response?.data?.message || "فشل عملية التحويل",
            position: "bottomCenter",
          });
        });
    },
    cancelOrder() {
      this.cancelDialog = true;
    },
    cancelConfirm() {
      if (!this.cancel_reason) {
        this.$iziToast.error({
          timeout: 5000,
          message: "يرجي ادخال سبب الإلغاء",
          position: "bottomRight",
        });
        return;
      } else {
        this.cancelLoading = true;
        this.$axios
          .post(`order/${this.id}/cancel`, {
            order_type: "Package",
            cancel_text: this.cancel_reason,
          })
          .then(() => {
            this.cancelLoading = false;
            this.cancelDialog = false;
            this.setRows();
          })
          .catch(() => {
            this.cancelLoading = false;
          });
      }
    },
    replaceImage(e) {
      e.target.src = imageFalse;
    },
    getDrivers() {
      this.$axios
        .get(`order/${this.id}/get_drivers_candidates?order_type=package`)
        .then((res) => {
          this.drivers = res.data.data.map((driver) => {
            const name = driver.name || driver.full_name || "";
            const distanceVal = parseFloat(driver.distance);
            const distanceStr = !isNaN(distanceVal)
              ? ` (${distanceVal.toFixed(2)} كم)`
              : "";
            return {
              ...driver,
              name: `${name}${distanceStr}`,
            };
          });
        });
    },
    assignDriver(driver_id) {
      const frmData = new FormData();
      frmData.append("order_type", "package");
      frmData.append("driver_id", driver_id);
      this.$axios
        .post(`order/${this.id}/assign_driver`, frmData)
        .then((res) => {
          this.setRows();
          this.$iziToast.success({
            timeout: 5000,
            message: res.data.message,
            position: "bottomRight",
          });
        })
        .catch((err) => {
          this.$iziToast.error({
            timeout: 5000,
            message: err.response.data.message,
            position: "bottomRight",
          });
        });
    },
    setRows() {
      this.loading = true;
      this.$axios({
        method: "GET",
        url: "/package_order/" + this.id,
      })
        .then((res) => {
          this.allData = res.data.data;
          this.loading = false;
          if (this.allData.status != "driver_finished_package") {
            this.getDrivers();
          }
          if (this.allData?.client?.id && this.allData?.driver?.id) {
            this.getChat();
          }
          if (this.allData?.driver?.lat && this.allData?.driver?.lng) {
            this.marker = {
              lat: parseFloat(this.allData.driver.lat),
              lng: parseFloat(this.allData.driver.lng),
            };
          }
          if (
            this.allData?.expected_route &&
            this.allData.expected_route.length > 0
          ) {
            this.center = {
              lat: parseFloat(this.allData.expected_route[0].lat),
              lng: parseFloat(this.allData.expected_route[0].lng),
            };
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },
    getChat() {
      this.$axios({
        method: "GET",
        url: `order/${this.allData?.chat_order_id}/show_chat?sender_id=${this.allData?.client?.id}&receiver_id=${this.allData?.driver?.id}`,
      }).then((res) => {
        this.chatData = res.data.data;
      });
    },
    mapLogic() {
      const socket = io(`${process.env.VUE_APP_BASE_URL_SOCKET}:3073`);
      this.socket = socket;
      socket.on("connect", () => {
        if (this.allData?.driver?.id) {
          socket.emit("join_update_location", {
            driver_id: this.allData.driver.id,
            dashboard: true,
          });
        }
      });

      const handleLocationUpdate = (data) => {
        if (!data) return;
        const driverId = data.driver_id;
        const currentDriverId = this.allData?.driver?.id;

        if (
          (data.active_order_id == this.id &&
            data.active_order_type == "package") ||
          (currentDriverId && driverId == currentDriverId)
        ) {
          socket.emit("join_update_location", {
            driver_id: driverId,
            dashboard: true,
          });
          this.marker = {
            lat: parseFloat(data.lat),
            lng: parseFloat(data.lng),
          };
        }
      };

      socket.on("update_location", handleLocationUpdate);
      socket.on("driver_location_update", handleLocationUpdate);

      socket.on(`admin_order_updated:${this.id}`, (data) => {
        if (
          data &&
          data.notify_type === "order_details_updated" &&
          data.order_id == this.id &&
          data.order_type == "package"
        ) {
          this.setRows();
        }
      });
    },
    goToPartnerPage(e) {
      if (!e) return;
      let route = this.$router.resolve({ path: `/driver/show/${e}` });
      window.open(route.href, "_blank");
    },
    onInvoiceFileChange(e) {
      const file = e.target.files[0];
      if (file) {
        this.invoiceFile = file;
        this.invoiceImagePreview = URL.createObjectURL(file);
      }
    },
    clearInvoiceFile() {
      this.invoiceFile = null;
      this.invoiceImagePreview = null;
      if (this.$refs.invoiceFileInput) {
        this.$refs.invoiceFileInput.value = "";
      }
    },
    submitInvoice() {
      if (!this.invoiceFile) {
        this.$iziToast.error({
          message:
            this.lang === "ar"
              ? "صورة الفاتورة مطلوبة"
              : "Invoice image is required",
          position: "bottomRight",
        });
        return;
      }
      this.isSubmittingInvoice = true;

      const formData = new FormData();
      formData.append("invoice_price", this.invoiceCost);
      formData.append("invoice_image", this.invoiceFile);

      this.$axios
        .post(`order/${this.id}/upload-invoice`, formData)
        .then((res) => {
          this.$iziToast.success({
            timeout: 3000,
            message:
              res.data.message ||
              (this.lang === "ar"
                ? "تم رفع الفاتورة بنجاح"
                : "Invoice raised successfully"),
            position: "bottomRight",
          });
          this.invoiceDialog = false;
          this.invoiceCost = "";
          this.invoiceFile = null;
          this.invoiceImagePreview = null;
          this.isSubmittingInvoice = false;
          this.setRows();
        })
        .catch((err) => {
          this.$iziToast.error({
            timeout: 3000,
            message:
              err.response?.data?.message ||
              (this.lang === "ar"
                ? "فشل رفع الفاتورة"
                : "Failed to raise invoice"),
            position: "bottomRight",
          });
          this.isSubmittingInvoice = false;
        });
    },
    getStatusTranslation(status) {
      if (!status) return "";
      const statusMap = {
        pending: "الجديدة",
        driver_accept: "مقبول من السائق",
        driver_start_order: "بدأ التوصيل",
        driver_pre_finished: "وصول السائق",
        driver_finished_package: "تم الانتهاء",
        client_cancel: "ملغى من العميل",
        admin_cancel: "ملغى من الإدارة",
        admin_returned: "مرتجع من الإدارة",
      };
      return statusMap[status] || status;
    },
    getNextStatusLabel(nextStatus) {
      if (!nextStatus) return "";
      if (typeof nextStatus === "object") {
        return nextStatus.label || this.getStatusTranslation(nextStatus.value);
      }
      return this.getStatusTranslation(nextStatus);
    },
    changeOrderStatus() {
      if (!this.allData?.next_available_status) return;

      let nextStatusValue = "";
      if (typeof this.allData.next_available_status === "object") {
        nextStatusValue = this.allData.next_available_status.value;
      } else {
        nextStatusValue = this.allData.next_available_status;
      }

      this.isChangeStatusLoading = true;
      this.$axios
        .put(`order/${this.id}/change-status`, {
          status: nextStatusValue,
        })
        .then((res) => {
          this.isChangeStatusLoading = false;
          this.$iziToast.success({
            timeout: 3000,
            message: res.data.message || "تم تغيير حالة الطلب بنجاح",
            position: "bottomRight",
          });
          this.setRows();
        })
        .catch((err) => {
          this.isChangeStatusLoading = false;
          this.$iziToast.error({
            timeout: 3000,
            message: err.response?.data?.message || "فشل تغيير حالة الطلب",
            position: "bottomRight",
          });
        });
    },
  },
  created() {
    this.setRows();
    this.mapLogic();
    this.getCancelReasons();
  },
  beforeDestroy() {
    if (this.socket) {
      this.socket.off(`admin_order_updated:${this.id}`);
      this.socket.disconnect();
      this.socket = null;
    }
  },
};
</script>

<style lang="scss" scoped>
.delivery-order-details-wrapper {
  padding: 10px 0;

  /* Top Hero Bar */
  .order-hero-card {
    background: #ffffff;
    border-radius: 20px;
    padding: 20px 24px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);
    border: 1px solid rgba(0, 0, 0, 0.04);

    .order-id-badge {
      font-size: 16px;
      color: #475569;
      display: inline-flex;
      align-items: center;

      .order-num {
        font-size: 19px;
        color: var(--mainColor-2, #783d73);
        margin-right: 4px;
      }
    }

    .status-badge-chip {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      background: rgba(120, 61, 115, 0.1);
      color: var(--mainColor-2, #783d73);
      padding: 6px 16px;
      border-radius: 30px;
      font-size: 13px;
      font-weight: 700;

      .pulse-indicator {
        width: 8px;
        height: 8px;
        background-color: var(--mainColor-2, #783d73);
        border-radius: 50%;
      }
    }

    .created-time-tag {
      font-size: 13px;
      color: #94a3b8;
      display: inline-flex;
      align-items: center;
    }

    .custom-hero-btn {
      font-weight: 700;
      font-size: 13px;
      letter-spacing: 0;
      text-transform: none;
      height: 42px !important;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06) !important;
      transition: transform 0.2s ease, box-shadow 0.2s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12) !important;
      }
    }

    .select-driver-container {
      min-width: 180px;

      ::v-deep .v-input__control {
        min-height: 42px !important;
      }

      ::v-deep .v-input__slot {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
        border: 1px solid rgba(0, 0, 0, 0.08) !important;
        padding-inline: 16px !important;
      }
    }
  }

  /* Quick KPI Metric Cards */
  .kpi-metric-card {
    background: #ffffff;
    border-radius: 18px;
    padding: 16px 20px;
    display: flex;
    align-items: center;
    gap: 16px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.03);
    border: 1px solid rgba(0, 0, 0, 0.03);

    .kpi-icon {
      width: 50px;
      height: 50px;
      border-radius: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;
      flex-shrink: 0;

      &.icon-purple {
        background: rgba(120, 61, 115, 0.1);
        color: #783d73;
      }

      &.icon-blue {
        background: rgba(14, 165, 233, 0.1);
        color: #0ea5e9;
      }

      &.icon-green {
        background: rgba(16, 185, 129, 0.1);
        color: #10b981;
      }

      &.icon-orange {
        background: rgba(245, 158, 11, 0.1);
        color: #f59e0b;
      }
    }

    .kpi-info {
      overflow: hidden;

      .kpi-label {
        font-size: 12px;
        color: #94a3b8;
        display: block;
        margin-bottom: 2px;
      }

      .kpi-value {
        font-size: 14px;
        font-weight: 700;
        color: #1e293b;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;

        &.price-text {
          font-size: 17px;
          color: #10b981;
          font-weight: 800;
        }
      }
    }
  }

  /* Badge status small */
  .badge-status-sm {
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 12px;
    color: white;
    font-weight: 600;
    margin-right: 4px;
  }

  /* Modern Card Enclosure */
  .modern-card {
    background: #ffffff;
    border-radius: 20px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
    border: 1px solid rgba(0, 0, 0, 0.03);
    overflow: hidden;

    .card-header-sm {
      padding: 16px 20px;
      background: linear-gradient(
        135deg,
        rgba(120, 61, 115, 0.04) 0%,
        rgba(120, 61, 115, 0.01) 100%
      );
      border-bottom: 1px solid rgba(0, 0, 0, 0.04);

      .title {
        font-size: 15px;
        font-weight: 700;
        color: #1e293b;
      }
    }

    .card-body-sm {
      padding: 20px;
    }

    &.border-left-danger {
      border-right: 4px solid #f44336;
    }
  }

  /* Profile Cards */
  .profile-side-card {
    .profile-avatar-wrap {
      display: inline-block;

      .profile-avatar {
        box-shadow: 0 8px 20px rgba(120, 61, 115, 0.15);
        border: 3px solid #ffffff;
      }

      .store-icon-wrap {
        width: 80px;
        height: 80px;
        border-radius: 20px;
        overflow: hidden;
        margin: 0 auto;
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }
    }

    .gender-pill {
      background: #f1f5f9;
      color: #64748b;
      font-size: 12px;
      padding: 2px 10px;
      border-radius: 12px;
      font-weight: 600;
    }

    .phone-link {
      text-decoration: none;
      font-size: 14px;
      transition: opacity 0.2s ease;

      &:hover {
        opacity: 0.8;
      }
    }
  }

  /* Media Preview Box */
  .media-preview-box {
    border-radius: 14px;
    overflow: hidden;
    max-height: 220px;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  /* Info Grid List */
  .info-grid-list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 16px;

    .info-item {
      background: #f8fafc;
      padding: 12px 16px;
      border-radius: 14px;
      border: 1px solid rgba(0, 0, 0, 0.02);

      .info-label {
        font-size: 12px;
        color: #94a3b8;
        display: block;
        margin-bottom: 2px;
      }

      .info-value {
        font-size: 14px;
        font-weight: 700;
        color: #1e293b;

        &.status-tag {
          color: var(--mainColor-2, #783d73);
        }
      }
    }
  }

  /* Receipt Card */
  .receipt-card {
    .receipt-list {
      .receipt-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 0;
        font-size: 14px;
        border-bottom: 1px dashed rgba(0, 0, 0, 0.05);

        &:last-child {
          border-bottom: none;
        }

        .label {
          color: #64748b;
        }
      }

      .receipt-total-box {
        background: linear-gradient(
          135deg,
          rgba(16, 185, 129, 0.1) 0%,
          rgba(16, 185, 129, 0.04) 100%
        );
        border: 1px solid rgba(16, 185, 129, 0.2);
        padding: 16px;
        border-radius: 16px;
        display: flex;
        justify-content: space-between;
        align-items: center;

        .label {
          font-weight: 700;
          color: #065f46;
          font-size: 15px;
        }

        .total-amount {
          font-size: 20px;
          font-weight: 800;
          color: #10b981;
        }
      }
    }
  }

  /* Modern Dialog Card */
  .modern-dialog-card {
    border-radius: 24px !important;

    .custom-input-field,
    .custom-textarea-field {
      border-radius: 12px;
      border: 1px solid rgba(0, 0, 0, 0.15);
      padding: 12px 14px;
      font-size: 14px;

      &:focus {
        border-color: #783d73;
        box-shadow: 0 0 0 3px rgba(120, 61, 115, 0.15);
      }
    }
  }

  /* Custom File Upload Box */
  .custom-file-upload-box {
    border: 2px dashed rgba(120, 61, 115, 0.3);
    background: #faf7fa;
    border-radius: 16px;
    transition: all 0.2s ease;

    &:hover {
      border-color: #783d73;
      background: #f5eff5;
    }

    .remove-upload-btn {
      position: absolute;
      top: 10px;
      right: 10px;
      background: rgba(244, 67, 54, 0.9);
      color: #fff;
      border: none;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    }
  }

  /* Tabs Section Card */
  .tabs-section-card {
    .tabs-header-bar {
      border-top-left-radius: 18px;
      border-top-right-radius: 18px;
    }

    .custom-nav-pills {
      .custom-tab-btn {
        border-radius: 12px !important;
        font-weight: 700;
        font-size: 14px;
        color: #555;
        background: transparent;
        border: 1px solid transparent;
        padding: 10px 16px;
        transition: all 0.25s ease;
        cursor: pointer;

        &:hover {
          background: rgba(120, 61, 115, 0.05);
          color: #783d73;
        }

        &.active {
          background: linear-gradient(
            135deg,
            var(--mainColor-2, #783d73),
            #9b4f95
          ) !important;
          color: #ffffff !important;
          box-shadow: 0 4px 14px rgba(120, 61, 115, 0.3);

          .badge {
            background-color: rgba(255, 255, 255, 0.25) !important;
            color: #ffffff !important;
          }
        }
      }
    }
  }

  .dir-ltr {
    direction: ltr;
  }

  /* Horizontal Timeline Stepper */
  .timeline-card-wrapper {
    .horizontal-timeline-scroll {
      overflow-x: auto;
      padding-bottom: 8px;

      &::-webkit-scrollbar {
        height: 6px;
      }
      &::-webkit-scrollbar-thumb {
        background: rgba(120, 61, 115, 0.2);
        border-radius: 10px;
      }
    }

    .horizontal-stepper {
      display: flex;
      align-items: flex-start;
      min-width: 100%;
      padding-top: 10px;
      padding-bottom: 10px;

      .stepper-item {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        flex: 1;
        min-width: 160px;
        padding: 0 10px;
        text-align: center;

        .stepper-line {
          position: absolute;
          top: 20px;
          right: 50%;
          width: 100%;
          height: 3px;
          background: linear-gradient(90deg, #783d73, #e0a3d7);
          z-index: 1;
        }

        .stepper-icon-circle {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: linear-gradient(
            135deg,
            var(--mainColor-2, #783d73),
            #9b4f95
          );
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          z-index: 2;
          box-shadow: 0 4px 12px rgba(120, 61, 115, 0.3);
          transition: transform 0.2s ease;

          &:hover {
            transform: scale(1.15);
          }
        }

        .stepper-details {
          margin-top: 12px;
          display: flex;
          flex-direction: column;
          align-items: center;

          .stepper-title {
            font-size: 13px;
            font-weight: 700;
            color: #2c3e50;
            margin-bottom: 4px;
            text-align: center;
          }

          .stepper-time {
            font-size: 11px;
            color: #777;
            background: #f0f2f5;
            padding: 2px 10px;
            border-radius: 12px;
            text-align: center;
            white-space: nowrap;
          }
        }
      }
    }
  }

  /* Responsive Adjustments */
  @media (max-width: 768px) {
    .order-hero-card {
      padding: 16px;

      .custom-hero-btn {
        width: 100%;
        justify-content: center;
      }

      .select-driver-container {
        width: 100%;
      }
    }

    .timeline-card-wrapper {
      .horizontal-timeline-scroll {
        overflow-x: visible;
      }

      .horizontal-stepper {
        flex-direction: column;
        align-items: flex-start;
        padding-right: 15px;

        .stepper-item {
          flex-direction: row;
          align-items: flex-start;
          width: 100%;
          min-width: unset;
          padding: 0 0 24px 0;
          text-align: right;

          &:last-child {
            padding-bottom: 0;
          }

          .stepper-line {
            top: 40px;
            bottom: 0;
            right: 19px;
            left: auto;
            width: 3px;
            height: calc(100% - 16px);
            background: linear-gradient(180deg, #783d73, #e0a3d7);
          }

          .stepper-icon-circle {
            width: 38px;
            height: 38px;
            font-size: 13px;
            flex-shrink: 0;
          }

          .stepper-details {
            margin-top: 0;
            margin-right: 16px;
            align-items: flex-start;

            .stepper-title {
              text-align: right;
              font-size: 14px;
            }

            .stepper-time {
              text-align: right;
            }
          }
        }
      }
    }
  }
  /* Soft Badges */
  .badge-light-primary {
    background-color: rgba(120, 61, 115, 0.1) !important;
    color: var(--mainColor-2, #783d73) !important;
    border: 1px solid rgba(120, 61, 115, 0.18) !important;
  }
  .badge-light-secondary {
    background-color: #f1f5f9 !important;
    color: #475569 !important;
    border: 1px solid #e2e8f0 !important;
    padding: 5px 12px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
  }
  .badge-light-info {
    background-color: rgba(2, 132, 199, 0.1) !important;
    color: #0284c7 !important;
    border: 1px solid rgba(2, 132, 199, 0.2) !important;
  }
  .badge-light-success {
    background-color: rgba(16, 185, 129, 0.1) !important;
    color: #059669 !important;
    border: 1px solid rgba(16, 185, 129, 0.2) !important;
  }
  .badge-light-warning {
    background-color: rgba(217, 119, 6, 0.1) !important;
    color: #d97706 !important;
    border: 1px solid rgba(217, 119, 6, 0.2) !important;
  }
  .badge-light-danger {
    background-color: rgba(239, 68, 68, 0.1) !important;
    color: #dc2626 !important;
    border: 1px solid rgba(239, 68, 68, 0.2) !important;
  }
}
</style>
